import { createContext, useCallback, useContext, useMemo, useState } from "react";

interface VoiceAssistantStateContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  voiceEngaged: boolean;
  setVoiceEngaged: (engaged: boolean) => void;
  vadState: "idle" | "listening" | "speaking" | "thinking";
  setVadState: (state: "idle" | "listening" | "speaking" | "thinking") => void;
  /** When true the widget should start voice immediately without opening the panel. */
  pendingVoiceLaunch: boolean;
  setPendingVoiceLaunch: (v: boolean) => void;
  /**
   * Task #116 — Master Schedule's Tour Planning drawer state lives here so the
   * floating AI Assistant orb (widget) and the drawer can render a single
   * shared Framer-Motion `layoutId` element in the same render commit. That's
   * what lets the orb morph into the drawer (and back) instead of two
   * separate fade animations. localStorage persistence is kept in this
   * provider so the value is initialized synchronously on first render.
   */
  tourPanelOpen: boolean;
  setTourPanelOpen: (open: boolean) => void;
  /**
   * Task #116 — Whether the AI Assistant is actually enabled+visible for the
   * current user. Published by the VoiceAssistantWidget once `/api/voice-
   * assistant/config` resolves. Both the orb (widget) and the Tour Planning
   * drawer gate their shared `layoutId` on this — when the assistant is off
   * for the workspace there is no morph source and no morph target, so
   * neither side should claim the shared id (otherwise the drawer alone
   * holds the id, which violates the "no morph source exists" requirement).
   */
  assistantEnabled: boolean;
  setAssistantEnabled: (enabled: boolean) => void;
  /**
   * Task #117 — Calendar Hub side panel. Mirrors the tour-panel pattern:
   * when the Calendar Hub mounts its right-hand side panel, the floating
   * AI Assistant orb morphs into it (shared `layoutId`). Coach mode is
   * forced on for the embedded assistant via context (no segmented toggle).
   *
   * - `calendarPanelMounted`: true while the Calendar Hub side panel exists
   *   on screen, regardless of which view (Coach vs Conflicts) is active.
   *   Used to suppress the FAB and gate the shared layoutId.
   * - `calendarCoachTarget`: the DOM node inside the panel that the Coach
   *   view exposes for the assistant widget to portal its UI into. Non-null
   *   only when the Coach view is the active inner view.
   *
   * Neither is persisted to localStorage — they reset every page load so
   * navigating away from /calendars naturally returns the FAB to its
   * floating state and unbinds the embedded assistant.
   */
  calendarPanelMounted: boolean;
  setCalendarPanelMounted: (mounted: boolean) => void;
  calendarCoachTarget: HTMLElement | null;
  setCalendarCoachTarget: (node: HTMLElement | null) => void;
  /**
   * Callback registered by ConflictsPanel so external callers (e.g. the AI
   * orb) can programmatically expand the Calendar Hub side panel to the Coach
   * tab without having to lift `manualCollapsed` / `view` state all the way
   * up. Set to null whenever the panel is unmounted or the page navigates away.
   */
  openCalendarCoachPanel: (() => void) | null;
  setOpenCalendarCoachPanel: (fn: (() => void) | null) => void;
}

const VoiceAssistantStateContext = createContext<VoiceAssistantStateContextValue | null>(null);

export function VoiceAssistantStateProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const [voiceEngaged, setVoiceEngaged] = useState(false);
  const [vadState, setVadState] = useState<"idle" | "listening" | "speaking" | "thinking">("idle");
  const [pendingVoiceLaunch, setPendingVoiceLaunch] = useState(false);
  const [tourPanelOpen, setTourPanelOpenState] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    try {
      return window.localStorage.getItem("tour-panel-open") === "true";
    } catch {
      return false;
    }
  });
  const setTourPanelOpen = useCallback((v: boolean) => {
    setTourPanelOpenState(v);
    if (typeof window !== "undefined") {
      try { window.localStorage.setItem("tour-panel-open", String(v)); } catch {}
    }
  }, []);
  const [assistantEnabled, setAssistantEnabled] = useState(false);
  const [calendarPanelMounted, setCalendarPanelMounted] = useState(false);
  const [calendarCoachTarget, setCalendarCoachTarget] = useState<HTMLElement | null>(null);
  const [openCalendarCoachPanel, setOpenCalendarCoachPanelState] = useState<(() => void) | null>(null);
  const setOpenCalendarCoachPanel = useCallback((fn: (() => void) | null) => {
    setOpenCalendarCoachPanelState(() => fn);
  }, []);

  const value = useMemo(
    () => ({
      open, setOpen,
      voiceEngaged, setVoiceEngaged,
      vadState, setVadState,
      pendingVoiceLaunch, setPendingVoiceLaunch,
      tourPanelOpen, setTourPanelOpen,
      assistantEnabled, setAssistantEnabled,
      calendarPanelMounted, setCalendarPanelMounted,
      calendarCoachTarget, setCalendarCoachTarget,
      openCalendarCoachPanel, setOpenCalendarCoachPanel,
    }),
    [
      open, voiceEngaged, vadState, pendingVoiceLaunch, tourPanelOpen,
      assistantEnabled, calendarPanelMounted, calendarCoachTarget,
      openCalendarCoachPanel, setTourPanelOpen, setOpenCalendarCoachPanel,
    ],
  );

  return (
    <VoiceAssistantStateContext.Provider value={value}>
      {children}
    </VoiceAssistantStateContext.Provider>
  );
}

export function useVoiceAssistantState() {
  const ctx = useContext(VoiceAssistantStateContext);
  if (!ctx) throw new Error("useVoiceAssistantState must be used within VoiceAssistantStateProvider");
  return ctx;
}
